import { AfterViewInit, Component, OnDestroy, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
	MatTableDataSource,
	MatTable,
	MatNoDataRow,
	MatColumnDef,
	MatHeaderCellDef,
	MatHeaderCell,
	MatCellDef,
	MatCell,
	MatHeaderRowDef,
	MatHeaderRow,
	MatRowDef,
	MatRow,
} from '@angular/material/table';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { DataSetComponent } from 'src/app/components/data-set/data-set.component';
import {
	AccessReportExportStatus,
	AccessReportRecord,
} from 'src/app/models/model';
import {
	ApiLoadingKeys,
	ApiService,
	QueryParam,
} from 'src/app/services/api.services';
import { LoadingIndicatorService } from 'src/app/services/loading-indicator.service';
import { TimeZoneService } from 'src/app/services/timezone.service';
import {
	AccessReportDialogComponent,
	AccessReportFilterOptions,
	AccessReportFilters,
	defaultAccessReportStartDate,
} from '../access-report-dialog/access-report-dialog.component';
import {
	AlarmStatus,
	alarmOverrideText,
	armedAlarmText,
	disarmedAlarmText,
} from 'src/app/models/data/ajax';
import { UserProfileService } from '../../../services/user-profile.service';
import { AppNavigationHeaderComponent } from '../../../components/navigation-header/app-navigation-header.component';
import {
	DataSetComponent as DataSetComponent_1,
	DataSetToolbarComponent,
	DataSetPrimaryActionGroupDirective,
	DataSetActionButtonComponent,
	DataSetContentComponent,
} from '../../../components/data-set/data-set.component';
import { BreadcrumbLabelDirective } from '../../../directives/breadcrumb-label.directive';
import { DataSetTableComponent } from '../../../components/data-set/data-set-table.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { NgClass, DatePipe } from '@angular/common';
import {
	AddressPipe,
	AccessTypeFormatPipe,
	ShortTimezonePipe,
} from '../../../components/pipes';
import { DataSetLoadingRowComponent } from '../../../components/data-set/data-set-loading-row.component';

@Component({
	selector: 'app-access-report-table',
	templateUrl: './access-report-table.component.html',
	styleUrls: ['./access-report-table.component.scss'],
	imports: [
		AppNavigationHeaderComponent,
		DataSetComponent_1,
		BreadcrumbLabelDirective,
		DataSetToolbarComponent,
		DataSetPrimaryActionGroupDirective,
		DataSetActionButtonComponent,
		DataSetContentComponent,
		DataSetTableComponent,
		MatTable,
		MatSort,
		MatNoDataRow,
		MatColumnDef,
		MatHeaderCellDef,
		MatHeaderCell,
		MatSortHeader,
		MatCellDef,
		MatCell,
		MatHeaderRowDef,
		MatHeaderRow,
		MatRowDef,
		MatRow,
		NgClass,
		DatePipe,
		AddressPipe,
		AccessTypeFormatPipe,
		ShortTimezonePipe,
		DataSetLoadingRowComponent,
		MatButton,
		MatTooltip,
	],
})
export class AccessReportTableComponent implements AfterViewInit, OnDestroy {
	@ViewChild('dataSet') dataSet!: DataSetComponent;

	AlarmStatus = AlarmStatus;
	displayedColumns: string[] = [
		'deviceSerialNumber',
		'deviceOwnerCompanyName',
		'accessedByCompanyName',
		'accessedByUserName',
		'propertyName',
		'deviceLocation',
		'address',
		'visitStartDate',
		'visitEndDate',
		'accessType',
	];
	dataSource = new MatTableDataSource<AccessReportRecord>();
	private filterSubscription?: Subscription;

	reportFilters: AccessReportFilters;
	compiledFilters: QueryParam[] = [];
	hasFilters = false;
	filterOptions?: AccessReportFilterOptions;

	breadcrumbLabel = $localize`:@@accessReportTable-breadcrumbLabel:Access Report`;

	generateAccessReportLoadingKey = ApiLoadingKeys.generateAccessReport;
	startAccessReportExportLoadingKey = ApiLoadingKeys.startAccessReportExport;
	downloadAccessReportExportLoadingKey =
		ApiLoadingKeys.downloadAccessReportExport;
	finishedLoadingInitData: boolean = false;

	exportNoRecordsTooltip = $localize`:@@accessReport-exportNoRecords:No records to export.`;

	/** True after the report has loaded with an empty table. */
	get exportHasNoRows(): boolean {
		return this.finishedLoadingInitData && this.dataSource.data.length === 0;
	}

	/** Disabled once the report has loaded and the table has no rows. Stays enabled while that request is still out. */
	get startExportDisabled(): boolean {
		return (
			this.loadingService.isLoading(this.startAccessReportExportLoadingKey) ||
			this.exportHasNoRows
		);
	}

	exportStatus?: AccessReportExportStatus;
	/** Download was clicked while an earlier export was still being prepared. */
	exportStillRunning = false;
	private exportPollTimer?: ReturnType<typeof setTimeout>;
	static readonly EXPORT_POLL_MS = 15000;

	disarmedAlarmText = disarmedAlarmText;
	armedAlarmText = armedAlarmText;
	alarmOverrideText = alarmOverrideText;

	isCareCompany = false;

	constructor(
		private readonly router: Router,
		private readonly dialog: MatDialog,
		private readonly apiService: ApiService,
		private readonly userProfileService: UserProfileService,
		public readonly loadingService: LoadingIndicatorService,
		readonly timeZoneService: TimeZoneService
	) {
		this.reportFilters =
			this.router.lastSuccessfulNavigation?.extras?.state?.reportFilters;
		this.compiledFilters =
			this.router.lastSuccessfulNavigation?.extras?.state?.compiledFilters;
		this.hasFilters =
			this.router.lastSuccessfulNavigation?.extras?.state?.hasFilters;
		this.filterOptions =
			this.router.lastSuccessfulNavigation?.extras?.state?.filterOptions;
		if (this.compiledFilters == null) {
			const start = defaultAccessReportStartDate();
			this.compiledFilters = [
				{
					param: 'visitStartDate',
					value: start.toISOString().split('T')[0],
				},
			];
			this.reportFilters = { visitStartDate: start };
			this.hasFilters = true;
		}
	}

	ngAfterViewInit(): void {
		this.dataSet.applyFilters();
		this.filterSubscription = this.dataSet.queryParams
			.pipe(
				mergeMap(async (filter) => {
					const result = await this.apiService.generateAccessReport([
						...filter,
						...(this.compiledFilters ?? []),
					]);
					this.finishedLoadingInitData = true;
					return result;
				})
			)
			.subscribe((response) => {
				this.dataSource.data = response;
			});
		this.refreshExportStatus().catch((error) =>
			console.error(
				'Something went wrong while loading the export status:',
				error?.message
			)
		);
		this.checkCareCompany()
			.then(() => {
				if (this.isCareCompany) {
					this.displayedColumns.splice(4, 0, 'clientName', 'clientIdn');
				}
			})
			.catch((error) =>
				console.error(
					'Something went wrong while checking care company:',
					error.message
				)
			);
	}

	async checkCareCompany(): Promise<void> {
		const userProfile = await this.userProfileService.getUserProfile();
		const activeCompanyId = userProfile?.activeRoleDetails?.company?.id;

		if (activeCompanyId) {
			const companyData = await this.apiService.getCompanyById(activeCompanyId);
			this.isCareCompany = companyData?.company?.companyType === 'care';
		}
	}

	ngOnDestroy(): void {
		this.filterSubscription?.unsubscribe();
		this.filterSubscription = undefined;
		this.stopExportPolling();
	}

	onRefineFiltersClicked(): void {
		this.dialog
			.open(AccessReportDialogComponent, {
				panelClass: 'drawer-dialog',
				data: {
					reportFilters: this.reportFilters,
					filterOptions: this.filterOptions,
				},
			})
			.afterClosed()
			.subscribe((_: any) => {
				this.dataSet.applyFilters();
			});
	}

	async onDownloadClicked(): Promise<void> {
		if (this.startExportDisabled) {
			return;
		}
		const result = await this.apiService.startAccessReportExport(
			this.compiledFilters
		);
		this.exportStillRunning = !result.started;
		this.exportStatus = result.export;
		this.scheduleExportPoll();
	}

	async onDownloadExportClicked(): Promise<void> {
		await this.apiService.downloadAccessReportExport();
	}

	async refreshExportStatus(): Promise<void> {
		this.exportStatus = await this.apiService.getAccessReportExport();
		if (
			this.exportStatus.status === 'ready' ||
			this.exportStatus.status === 'failed'
		) {
			this.exportStillRunning = false;
		}
		this.scheduleExportPoll();
	}

	private scheduleExportPoll(): void {
		this.stopExportPolling();
		if (
			this.exportStatus?.status !== 'preparing' &&
			this.exportStatus?.status !== 'waiting'
		) {
			return;
		}
		this.exportPollTimer = setTimeout(() => {
			this.refreshExportStatus().catch((error) =>
				console.error(
					'Something went wrong while loading the export status:',
					error?.message
				)
			);
		}, AccessReportTableComponent.EXPORT_POLL_MS);
	}

	private stopExportPolling(): void {
		if (this.exportPollTimer) {
			clearTimeout(this.exportPollTimer);
			this.exportPollTimer = undefined;
		}
	}

	rowId(row: AccessReportRecord): string {
		return row?.id || 'unknown-id';
	}
}
